import type { AxiosResponse } from 'axios'
import type { Ref } from 'vue'
import { ref } from 'vue'

import type {
    GetReportingParametersPayloadSchema,
    ReportingParametersResponseSchema
} from '@/api/schemas/reports/operatorReporting'
import { REPORTS } from '@/constants'
import { buildReportingParameterColumns, type ReportingParameterColumn } from '@/views/ReportsV2/helpers/reportingParameters'

export interface ReportingParameterColumnsState {
    reportingParameterColumns: Ref<ReportingParameterColumn[]>
    reportingParameterColumnsSiteId: Ref<number | null>
}

type FetchReportingParameters = (
    payload: GetReportingParametersPayloadSchema
) => Promise<AxiosResponse<ReportingParametersResponseSchema>>

export function createReportingParameterColumnsState(): ReportingParameterColumnsState {
    return {
        reportingParameterColumns: ref<ReportingParameterColumn[]>([]),
        reportingParameterColumnsSiteId: ref<number | null>(null)
    }
}

/**
 * Loads the task list columns configured for the selected production site. The configuration depends
 * only on the site, so a loaded one is reused; a failed load is not remembered, so the next one retries.
 */
export function createReportingParameterColumnsLoader(
    state: ReportingParameterColumnsState,
    getProductionSiteId: () => number | null,
    fetchReportingParameters: FetchReportingParameters
): () => Promise<void> {
    const setColumns = (columns: ReportingParameterColumn[], productionSiteId: number | null): void => {
        state.reportingParameterColumns.value = columns
        state.reportingParameterColumnsSiteId.value = productionSiteId
    }

    return async (): Promise<void> => {
        const productionSiteId = getProductionSiteId()

        if (!productionSiteId) {
            setColumns([], null)
            return
        }

        if (state.reportingParameterColumnsSiteId.value === productionSiteId) {
            return
        }

        try {
            const response = await fetchReportingParameters({
                productionSiteId,
                location: REPORTS.REPORTING_PARAMETERS.LOCATIONS.LIST
            })

            if (productionSiteId === getProductionSiteId()) {
                setColumns(buildReportingParameterColumns(response.data.results), productionSiteId)
            }
        } catch (error) {
            if (productionSiteId === getProductionSiteId()) {
                setColumns([], null)
            }

            throw error
        }
    }
}
